<template>
  <div class="p-postEditor__itemWrapper">
    <image-uploader-selecter
      :image-uploader-selecter="imageUploaderSelecter"
      :image-direct-url.sync="imageDirectUrl"
      @close-image-uploader-selecter="imageUploaderSelecter = false"
      @select-picture="updateImage"
      @upload-download-image="uploadDownloadImage"
      @upload-picture="uploadPicture"
    />
    <div v-if="editingItemIndex !== index" @click="editItem">
      <BannerItemPreview :item="item" />
    </div>
    <item-form v-if="editingItemIndex === index" :item="item">
      <v-row>
        <v-col lg="6">
          <v-banner class="pa-0" shaped :sticky="true" style="top: 20px">
            <div
              class="pa-3 d-flex"
              @mouseover="imageItemOverlay = true"
              @mouseleave="imageItemOverlay = false"
              style="height: 400px; border: 1px solid #ddd; border-radius: 3px"
            >
              <div class="d-flex" style="width: 100%; position: relative">
                <v-overlay absolute color="white" :value="imageItemOverlay">
                  <div
                    style="
                      display: flex;
                      justify-content: center;
                      gap: 10px;
                      height: 100%;
                      align-items: center;
                    "
                  >
                    <v-btn
                      class="ma-1"
                      fab
                      x-small
                      dark
                      @click.stop="imageUploaderSelecter = true"
                    >
                      <v-icon>mdi-cog</v-icon>
                    </v-btn>
                    <v-btn
                      v-if="true"
                      class="ma-1"
                      color="error"
                      fab
                      x-small
                      @click="deleteImage"
                    >
                      <v-icon>mdi-delete</v-icon>
                    </v-btn>
                  </div>
                </v-overlay>
                <BannerItemPreview
                  :item="{ itemableAttributes: editingItemable }"
                />
              </div>
            </div>
          </v-banner>
        </v-col>
        <v-col lg="6">
          <v-text-field
            v-model="url"
            autofocus
            :rules="[presenceValidation, urlValidation]"
          >
            <template v-slot:label>
              <InputLabel label-name="url" :required="true" />
            </template>
          </v-text-field>
          <v-text-field
            class='mb-3'
            v-model="maxWidth"
            type="number"
            suffix="px"
            autofocus
            clearable
            :min="375"
          >
            <template v-slot:label>
              <InputLabel label-name="横幅の最大値" :required="false" />
            </template>
          </v-text-field>
          <span class="d-block caption v-label theme--light mb-2"
            >上側のラベル</span
          >
          <v-textarea
            :value="topLabel.content"
            class="body-2"
            rows="2"
            outlined
            hide-details
            placeholder="テキスト"
            @input="
              (value) => updateEditingItemable(value, 'topLabel', 'content')
            "
          />
          <v-row class="mt-1 mb-4">
            <v-col cols="6">
              <v-text-field
                :value="topLabel.size"
                type="number"
                label="文字サイズ"
                suffix="px"
                dense
                :min="8"
                @input="
                  (value) => updateEditingItemable(value, 'topLabel', 'size')
                "
              >
                <template v-slot:label>
                  <InputLabel label-name="文字サイズ" :required="false" />
                </template>
              </v-text-field>
            </v-col>
            <v-col cols="6">
              <v-text-field
                :value="topLabel.color"
                placeholder="#ffffff"
                dense
                clearable
                :rules="[colorCodeValidation(true)]"
                @input="
                  (value) => updateEditingItemable(value, 'topLabel', 'color')
                "
              >
                <template v-slot:label>
                  <InputLabel label-name="文字色" :required="false" />
                </template>
                <template v-slot:append-outer>
                  <ColorPickerMenu
                    :color-code="topLabel.color"
                    @change-color-code="
                      (value) =>
                        updateEditingItemable(value, 'topLabel', 'color')
                    "
                  />
                </template>
              </v-text-field>
            </v-col>
          </v-row>
          <span class="d-block caption v-label theme--light mb-2"
            >下側のラベル</span
          >
          <v-textarea
            :value="bottomLabel.content"
            class="body-2"
            rows="2"
            outlined
            hide-details
            placeholder="テキスト"
            @input="
              (value) => updateEditingItemable(value, 'bottomLabel', 'content')
            "
          />
          <v-row class="mt-1 mb-4">
            <v-col cols="6">
              <v-text-field
                :value="bottomLabel.size"
                type="number"
                label="文字サイズ"
                suffix="px"
                dense
                :min="8"
                @input="
                  (value) => updateEditingItemable(value, 'bottomLabel', 'size')
                "
              >
                <template v-slot:label>
                  <InputLabel label-name="文字サイズ" :required="false" />
                </template>
              </v-text-field>
            </v-col>
            <v-col cols="6">
              <v-text-field
                :value="bottomLabel.color"
                placeholder="#ffffff"
                dense
                clearable
                :rules="[colorCodeValidation(true)]"
                @input="
                  (value) =>
                    updateEditingItemable(value, 'bottomLabel', 'color')
                "
              >
                <template v-slot:label>
                  <InputLabel label-name="文字色" :required="false" />
                </template>
                <template v-slot:append-outer>
                  <ColorPickerMenu
                    :color-code="bottomLabel.color"
                    @change-color-code="
                      (value) =>
                        updateEditingItemable(value, 'bottomLabel', 'color')
                    "
                  />
                </template>
              </v-text-field>
            </v-col>
          </v-row>
          <span class="d-block caption v-label theme--light mb-2">影</span>
          <div class="px-1 row" style="width: 100%">
            <v-text-field
              class="col-xl-3 col-6 pr-1"
              suffix="px"
              type="number"
              dense
              block
              :value="boxShadowX"
              @input="
                updateEditingItemable(stringToNumber($event), 'boxShadow', 'x')
              "
              label="左右"
            >
            </v-text-field>
            <v-text-field
              class="col-xl-3 col-6 pr-1"
              suffix="px"
              type="number"
              dense
              block
              :value="boxShadowY"
              @input="
                updateEditingItemable(stringToNumber($event), 'boxShadow', 'y')
              "
              label="上下"
            >
            </v-text-field>
            <v-text-field
              class="col-xl-3 col-6 pr-1"
              suffix="px"
              type="number"
              dense
              block
              :value="boxShadowBlur"
              @input="
                updateEditingItemable(
                  stringToNumber($event),
                  'boxShadow',
                  'blur'
                )
              "
              label="ぼかし"
            >
            </v-text-field>
            <v-text-field
              class="col-xl-3 col-6 pr-1"
              suffix="px"
              type="number"
              dense
              block
              :value="boxShadowSpread"
              @input="
                updateEditingItemable(
                  stringToNumber($event),
                  'boxShadow',
                  'spread'
                )
              "
              label="広がり"
            >
            </v-text-field>
          </div>
        </v-col>
      </v-row>
    </item-form>
  </div>
</template>

<script>
import itemUtilsMixin from '@/mixins/item_utils'
import imageUtilsMixin from '@/mixins/image_utils'
import FormValidationUtilsMixin from '@/mixins/form_validation_utils'
import ImageUploaderSelecter from '../parts/ImageUploaderSelecter'
import ImageItemPreview from './previews/ImageItemPreview'
import InputLabel from '../../commons/formParts/inputLabel'
import ColorPickerMenu from '@/components/commons/ColorPickerMenu'
import BannerItemPreview from './previews/BannerItemPreview'

export default {
  name: 'BannerItem',
  components: {
    BannerItemPreview,
    ColorPickerMenu,
    InputLabel,
    ImageItemPreview,
    ImageUploaderSelecter
  },
  mixins: [itemUtilsMixin, imageUtilsMixin, FormValidationUtilsMixin],
  data: () => {
    return {
      imageItemOverlay: false,
      imageUploaderSelecter: false
    }
  },
  computed: {
    url: {
      get: function () {
        return this.editingItemable.url
      },
      set: function (value) {
        this.updateEditingItemable(value, 'url')
      }
    },
    maxWidth: {
      get: function () {
        return this.editingItemable.maxWidth
      },
      set: function (value) {
        this.updateEditingItemable(value, 'maxWidth')
      }
    },
    imageDirectUrl: {
      get: function () {
        return this.editingItemable.imageDirectUrl
      },
      set: function (value) {
        this.updateEditingItemable(value, 'imageDirectUrl')
      }
    },
    topLabel () {
      return this.editingItemable.topLabel || {}
    },
    bottomLabel () {
      return this.editingItemable.bottomLabel || {}
    },
    boxShadowX () {
      return this.editingItemable.boxShadow
        ? this.editingItemable.boxShadow.x
        : null
    },
    boxShadowY () {
      return this.editingItemable.boxShadow
        ? this.editingItemable.boxShadow.y
        : null
    },
    boxShadowBlur () {
      return this.editingItemable.boxShadow
        ? this.editingItemable.boxShadow.blur
        : null
    },
    boxShadowSpread () {
      return this.editingItemable.boxShadow
        ? this.editingItemable.boxShadow.spread
        : null
    }
  },
  methods: {
    updateImage (picture) {
      let itemable = Object.assign({}, this.editingItemable)
      itemable.imagableId = picture.id
      itemable.imagableType = 'Picture'
      itemable.imagableUrl = picture.file.fitMiddle.url
      this.$store.commit('PostEditor/UPDATE_EDITING_ITEMABLE', itemable)
      this.imageUploaderSelecter = false
    },
    uploadDownloadImage (downloadImage) {
      let itemable = Object.assign({}, this.editingItemable)
      itemable.imagableId = downloadImage.id
      itemable.imagableType = 'DownloadImage'
      itemable.imagableUrl = downloadImage.file.fitMiddle.url
      this.$store.commit('PostEditor/UPDATE_EDITING_ITEMABLE', itemable)
      this.imageUploaderSelecter = false
    },
    uploadPicture (picture) {
      let itemable = Object.assign({}, this.editingItemable)
      itemable.imagableId = picture.id
      itemable.imagableType = 'Picture'
      itemable.imagableUrl = picture.file.fitMiddle.url
      this.$store.commit('PostEditor/UPDATE_EDITING_ITEMABLE', itemable)
      this.imageUploaderSelecter = false
    },
    deleteImage () {
      let itemable = Object.assign({}, this.editingItemable)
      itemable.imagableId = null
      itemable.imagableType = null
      itemable.imagableUrl = null
      itemable.imageDirectUrl = null
      this.$store.commit('PostEditor/UPDATE_EDITING_ITEMABLE', itemable)
    },
    stringToNumber (stringValue) {
      if (!stringValue) return

      return Number(stringValue)
    }
  }
}
</script>

<style scoped></style>
